import { useState } from 'react';
import { Loader2, Package, Clock, Layers, ListOrdered, ClipboardList } from 'lucide-react';
import { fmtTrvani, fmtCislo, SECTION_COLOR } from './receptUtils';
import KalkulacniKolac, { sestavKolac, KOLAC_BARVY } from './KalkulacniKolac';
import useRozmer from '../../hooks/useRozmer';

const KANALY = [
    { id: 'bistro', label: 'Bistro' },
    { id: 'rozvoz', label: 'Rozvoz' },
    { id: 'vydej',  label: 'Výdej' },
];

const NENULOVA = (p) => p.value > 0.005;

// Odhad výšek bloků (px) — podle nich se volí stupeň zhuštění. Koláč se pak
// roztáhne do toho, co reálně zbyde (měří se), takže odhad nemusí sedět na pixel.
const V = {
    telo: 32,           // p-4 nahoře + dole
    kanal: 46,          // přepínač + mezera
    cena: 49, cenaKompakt: 31,
    legendaObal: 34,    // odsazení + nadpis „Rozpad"
    oddelovac: 9,
    radek: 24, radekKompakt: 20,
    dlazdice: 158, dlazdiceRadek: 66,
    varovani: 29,
};
// Nejmenší koláč, se kterým stupeň 0–4 ještě počítá. Když se nevejde ani u
// stupně 4, nastoupí 5 a koláč dostane, co zbyde (pod 64 px se nekreslí).
const MIN_KOLAC = [200, 170, 130, 110, 90];

/**
 * Stupně zhuštění (0 = vše):
 *   1 — v rozpadu jen nenulové řádky
 *   2 — dlaždice v jednom řádku
 *   3 — hustší rozpad, cena na jeden řádek
 *   4 — bez dlaždic
 *   5 — bez řádku DPH a upozornění na balicí předpis
 */
function zvolStupen({ vyska, isPro, kolac, varovani }) {
    if (!vyska || !kolac) return 0;
    const naklady = kolac.polozky.filter(p => !['marze', 'dph'].includes(p.id));
    const maDph = kolac.polozky.some(p => p.id === 'dph');

    const odhad = (s) => {
        const radek = s >= 3 ? V.radekKompakt : V.radek;
        const radkuNakladu = s >= 1 ? naklady.filter(NENULOVA).length : naklady.length;
        const legenda = V.legendaObal + radek + V.oddelovac + radkuNakladu * radek + (maDph && s < 5 ? V.oddelovac + radek : 0);
        const dlazdice = s < 2 ? V.dlazdice : s < 4 ? V.dlazdiceRadek : 0;
        return V.telo + (isPro ? V.kanal : 0) + (s >= 3 ? V.cenaKompakt : V.cena) + legenda + dlazdice
            + (varovani && s < 5 ? V.varovani : 0);
    };

    for (let s = 0; s < 5; s++) {
        if (odhad(s) + MIN_KOLAC[s] <= vyska) return s;
    }
    return 5;
}

// Stálá karta vpravo: kalkulační koláč a legenda rozpadu.
// Balení i prodejní cena se liší kanál od kanálu, takže náklad i marže jsou
// vždycky „v kanálu" — jedno číslo by u dvou ze tří lhalo. Proto přepínač.
//
// Karta má pevnou velikost — vyplní výšku panelu, nikdy nescrolluje. Koláč se
// škáluje do zbylého místa a méně důležité bloky se podle výšky zhušťují.
export default function RecipeCalcCard({
    recept, kalkulace, isPro, isDark, mutedColor, secondaryColor, cardStyle,
    bomLength = 0, krokyLength = 0, pouzitoCount = 0, calcLoading = false, onPouzito,
    // Příplatek (ADD) se prodává, ale bez balení a kanálů — marže ano, přepínač ne.
    prodava = false,
}) {
    // Výchozí kanál je ten nejdražší — tam se marže láme nejdřív.
    const [kanal, setKanal] = useState(() => {
        const b = kalkulace?.baleni?.kanaly;
        if (!b) return 'rozvoz';
        return KANALY.map(k => k.id).reduce((a, k) => ((b[k] ?? 0) > (b[a] ?? 0) ? k : a), 'bistro');
    });
    const [teloRef, telo] = useRozmer();
    const [kolacRef, kolacPlocha] = useRozmer();

    const casS = (recept?.sop?.kroky || []).reduce((s, k) => s + (k.cas_s || 0), 0);
    const nakladBaleni = kalkulace?.baleni?.kanaly?.[kanal] ?? 0;
    const vKanalu = kalkulace?.kanaly?.[kanal];

    // Polotovar se neprodává — koláč je pak čistě nákladový, bez marže a DPH.
    const sCenou = isPro || prodava;
    const kolac = kalkulace ? sestavKolac(kalkulace, nakladBaleni, sCenou ? vKanalu : null) : null;
    const jednotka = kalkulace?.jednotka_zkratka || 'ks';
    const bezPredpisu = isPro && kalkulace && !kalkulace.baleni?.predpis;

    const stupen = zvolStupen({ vyska: telo.h, isPro, kolac, varovani: bezPredpisu });
    const hustaLegenda = stupen >= 3;
    // Koláč: čtverec do zbylé plochy, strop je šířka panelu. Nikdy nepřeroste
    // přidělenou plochu (jinak by přetekl do přepínače kanálů) — a když na něj
    // nezbyde ani 64 px, nekreslí se: marže je hned pod ním v rozpadu.
    const velikostKolace = kolacPlocha.w && kolacPlocha.h
        ? Math.floor(Math.min(kolacPlocha.w, kolacPlocha.h))
        : 220;
    const kreslitKolac = velikostKolace >= 64;

    const tileBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const tileBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

    const barvaMarze = (p) => p == null ? mutedColor : p >= 50 ? KOLAC_BARVY.marze : p >= 30 ? SECTION_COLOR : KOLAC_BARVY.odpad;

    /** Řádek legendy — tečka, název, částka, podíl na kruhu. */
    const legendaRadek = (p, { hero = false } = {}) => {
        const pct = kolac.zaklad > 0 ? (p.value / kolac.zaklad) * 100 : 0;
        return (
            <div key={p.id} className={`flex items-center gap-2 ${hustaLegenda ? 'py-0.5' : 'py-1'}`}>
                <span style={{
                    width: hero ? 10 : 8, height: hero ? 10 : 8, borderRadius: 2,
                    background: p.color, display: 'inline-block', flexShrink: 0,
                }} />
                <span className="text-[11px] flex-1 min-w-0 truncate"
                    style={{ color: hero ? 'var(--text-primary)' : secondaryColor, fontWeight: hero ? 700 : 400 }}>
                    {p.label}
                </span>
                <span className="text-[11px] tabular-nums flex-shrink-0"
                    style={{ color: hero ? p.color : 'var(--text-primary)', fontWeight: hero ? 700 : 600 }}>
                    {fmtCislo(p.value)}
                </span>
                <span className="text-[10px] tabular-nums flex-shrink-0 text-right"
                    style={{ color: mutedColor, width: 30 }}>
                    {pct.toFixed(0)} %
                </span>
            </div>
        );
    };

    const infoTile = (Icon, value, label, onClick = null, title = undefined, radek = false) => {
        const Wrap = onClick ? 'button' : 'div';
        return (
            <Wrap onClick={onClick || undefined} title={title || (radek ? label : undefined)}
                className={`rounded-lg flex flex-col items-center w-full ${radek ? 'py-1.5 gap-0.5' : 'py-2.5 gap-1'}`}
                style={{
                    background: tileBg, border: `1px solid ${tileBorder}`,
                    cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s', minWidth: 0,
                }}
                onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = SECTION_COLOR; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = tileBorder; }}>
                <Icon size={radek ? 13 : 15} style={{ color: onClick ? SECTION_COLOR : mutedColor }} />
                <div className={`${radek ? 'text-[12px]' : 'text-sm'} font-bold leading-none truncate max-w-full`}
                    style={{ color: 'var(--text-primary)', textDecoration: onClick ? 'underline' : 'none' }}>{value}</div>
                <div className="text-[9px] truncate max-w-full" style={{ color: mutedColor }}>{label}</div>
            </Wrap>
        );
    };

    const dlazdice = [
        [Package, bomLength, 'ingrediencí'],
        [ListOrdered, krokyLength, krokyLength === 1 ? 'krok' : 'kroků'],
        [Clock, fmtTrvani(casS) || '—', 'příprava'],
        [Layers, `${pouzitoCount}×`, 'použito',
            pouzitoCount > 0 && onPouzito ? onPouzito : null,
            pouzitoCount > 0 ? 'Zobrazit, ve kterých recepturách se používá' : undefined],
    ];

    const nakladyLegenda = kolac
        ? kolac.polozky.filter(p => !['marze', 'dph'].includes(p.id)).filter(p => stupen >= 1 ? NENULOVA(p) : true)
        : [];

    return (
        <div className="rounded-xl flex flex-col" style={{ ...cardStyle, flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ flexShrink: 0, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: secondaryColor }}>Kalkulace</span>
                {calcLoading
                    ? <span className="flex items-center gap-1 text-[10px]" style={{ color: SECTION_COLOR }}><Loader2 size={12} className="animate-spin" /> přepočítávám</span>
                    : kalkulace && <span className="text-[10px]" style={{ color: mutedColor }}>{kalkulace.mnozstvi || 1} {jednotka} / dávka</span>}
            </div>

            <div ref={teloRef} className="p-4" style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {!kalkulace ? (
                    <div className="flex-1 flex items-center justify-center text-center">
                        <p className="text-xs" style={{ color: mutedColor }}>Přidejte ingredience v Receptuře pro výpočet kalkulace.</p>
                    </div>
                ) : (
                    <>
                        {/* Kanál — náklad i marže se liší podle toho, jak položka odchází */}
                        {isPro && (
                            <div className="flex gap-1 mb-4 p-0.5 rounded-lg" style={{ flexShrink: 0, background: tileBg, border: `1px solid ${tileBorder}` }}>
                                {KANALY.map(k => {
                                    const aktivni = k.id === kanal;
                                    return (
                                        <button key={k.id} onClick={() => setKanal(k.id)}
                                            className="flex-1 rounded-md py-1.5 text-[11px] font-semibold"
                                            style={{
                                                background: aktivni ? (isDark ? 'rgba(255,255,255,0.10)' : '#fff') : 'none',
                                                color: aktivni ? 'var(--text-primary)' : mutedColor,
                                                border: 'none', cursor: 'pointer',
                                                boxShadow: aktivni ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                            }}>
                                            {k.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Koláč — celá koruna zákazníka: náklady tence, marže tlustě, DPH šedě.
                            Bere zbylou výšku; čtverec se vepíše do plochy, kterou dostane. */}
                        <div ref={kolacRef} style={{ flex: '1 1 0', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {kreslitKolac && (
                                <div style={{ width: velikostKolace, maxWidth: '100%' }}>
                                    <KalkulacniKolac {...kolac} jednotka={jednotka} mutedColor={mutedColor} maxSirka={velikostKolace} jenCislo={velikostKolace < 130} />
                                </div>
                            )}
                        </div>

                        {/* Kolik kruh představuje — bez toho jsou procenta v legendě bezprizorní */}
                        <div className={`text-center ${hustaLegenda ? 'mt-2 mb-2' : 'mt-2 mb-3'}`} style={{ flexShrink: 0 }}>
                            {kolac.maCenu ? (
                                <>
                                    <div className="text-[11px]" style={{ color: secondaryColor }}>
                                        {kolac.ztrata ? 'Náklady s DPH' : 'Prodejní cena'}
                                        {' '}<span className="font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmtCislo(kolac.zaklad)} Kč</span>
                                        {' '}s DPH
                                        {hustaLegenda && vKanalu.marze_pct != null && !kolac.ztrata && (
                                            <> · <span className="font-semibold" style={{ color: barvaMarze(vKanalu.marze_pct) }}>{vKanalu.marze_pct.toFixed(0)} %</span></>
                                        )}
                                    </div>
                                    {!hustaLegenda && (
                                        <div className="text-[10px]" style={{ color: mutedColor }}>
                                            {fmtCislo(vKanalu.prodejni_cena)} Kč bez DPH
                                            {vKanalu.dph_sazba > 0 && ` · DPH ${vKanalu.dph_sazba} %`}
                                            {vKanalu.marze_pct != null && !kolac.ztrata && (
                                                <> · marže <span className="font-semibold" style={{ color: barvaMarze(vKanalu.marze_pct) }}>{vKanalu.marze_pct.toFixed(0)} %</span> z ceny bez DPH</>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-[10px]" style={{ color: mutedColor }}>
                                    {isPro ? 'Bez prodejní ceny — marži není z čeho počítat.' : prodava ? 'Bez příplatku — marži není z čeho počítat.' : 'Polotovar se neprodává — kruh je jen náklad.'}
                                </div>
                            )}
                        </div>

                        {/* Legenda — jeden sloupec místo dlaždic; pořadí sedí na kruh */}
                        <div className="rounded-lg px-3 py-2" style={{ flexShrink: 0, background: tileBg, border: `1px solid ${tileBorder}` }}>
                            <div className="flex items-center justify-between text-[9px] uppercase tracking-wide pb-1"
                                style={{ color: mutedColor, borderBottom: `1px solid ${tileBorder}` }}>
                                <span>Rozpad</span>
                                <span>{kolac.maCenu ? 'Kč · % z ceny s DPH' : 'Kč · % z nákladu'}</span>
                            </div>

                            {kolac.polozky.filter(p => p.id === 'marze').map(p => legendaRadek(p, { hero: true }))}

                            {kolac.ztrata && (
                                <div className={`flex items-center gap-2 ${hustaLegenda ? 'py-0.5' : 'py-1'}`}>
                                    <span style={{ width: 10, height: 10, borderRadius: 2, background: KOLAC_BARVY.ztrata, display: 'inline-block', flexShrink: 0 }} />
                                    <span className="text-[11px] flex-1 font-bold" style={{ color: 'var(--text-primary)' }}>Ztráta</span>
                                    <span className="text-[11px] font-bold tabular-nums" style={{ color: KOLAC_BARVY.ztrata }}>{fmtCislo(kolac.marze)}</span>
                                    <span className="text-[10px] text-right" style={{ color: mutedColor, width: 30 }} />
                                </div>
                            )}

                            {nakladyLegenda.length > 0 && (
                                <div style={{ borderTop: `1px solid ${tileBorder}` }} className="mt-1 pt-1">
                                    {nakladyLegenda.map(p => legendaRadek(p))}
                                </div>
                            )}

                            {stupen < 5 && kolac.polozky.filter(p => p.id === 'dph').map(p => (
                                <div key={p.id} style={{ borderTop: `1px solid ${tileBorder}` }} className="mt-1 pt-1">
                                    {legendaRadek(p)}
                                </div>
                            ))}
                        </div>

                        {/* Info dlaždice — 2×2, na nižší kartě v jednom řádku, na nejnižší pryč */}
                        {stupen < 4 && (
                            <div className={`grid mt-3 ${stupen < 2 ? 'grid-cols-2 gap-2' : 'grid-cols-4 gap-1.5'}`} style={{ flexShrink: 0 }}>
                                {dlazdice.map(([Icon, value, label, onClick, title]) => (
                                    <div key={label} style={{ minWidth: 0 }}>{infoTile(Icon, value, label, onClick, title, stupen >= 2)}</div>
                                ))}
                            </div>
                        )}

                        {bezPredpisu && stupen < 5 && (
                            <div className="flex items-start gap-1.5 mt-3 text-[11px]" style={{ flexShrink: 0, color: KOLAC_BARVY.stroje }}>
                                <ClipboardList size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                                Bez balicího předpisu — balení se do nákladu nepočítá.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
